import { Injectable } from '@angular/core';
import { ApiUtilsService } from 'src/app/api/services/api-utils.service';
import { PresentationsService } from 'src/app/presentations/services/presentations.service';
import { cloneDeep } from 'lodash';
import { AlertSettings, Company } from 'src/app/api/services/company';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { CompanyIntegrationComponentService } from '../../models/integrations.types';

/** Alert settings plus client-only presentation display name. */
export type CapAlertSettings = AlertSettings & {
  presentationName?: string;
};

/** String-list fields updated via CSV / checkbox helpers in the CAP UI. */
export type CapAlertListField = {
  [K in keyof CapAlertSettings]-?: CapAlertSettings[K] extends string[]
    ? K
    : never;
}[keyof CapAlertSettings];

@Injectable({
  providedIn: 'root',
})
export class CapAlertsService implements CompanyIntegrationComponentService {
  isLoading = false;
  errorSaving = false;
  missingTemplate = false;
  errorMessage = '';
  apiError = '';
  alertKey!: string;
  alertSettings!: CapAlertSettings;
  changeDate?: Date;
  changedBy!: string;
  private _company!: Company;

  readonly textFieldsValues = [
    { key: 'headline', label: 'Headline' },
    { key: 'description', label: 'Description' },
    { key: 'instruction', label: 'Instruction' },
  ];

  readonly allowedStatusesValues = [
    { key: 'Actual', label: 'Actual' },
    { key: 'Exercise', label: 'Exercise' },
    { key: 'System', label: 'System' },
    { key: 'Test', label: 'Test' },
    { key: 'Draft', label: 'Draft' },
  ];

  readonly allowedCategoriesValues = [
    { key: 'Geo', label: 'Geophysical' },
    { key: 'Met', label: 'Meteorological' },
    { key: 'Safety', label: 'Safety' },
    { key: 'Security', label: 'Security' },
    { key: 'Rescue', label: 'Rescue' },
    { key: 'Fire', label: 'Fire' },
    { key: 'Health', label: 'Health' },
    { key: 'Env', label: 'Environment' },
    { key: 'Transport', label: 'Transport' },
    { key: 'Infra', label: 'Infrastructure' },
    {
      key: 'CBRNE',
      label: 'Chemical, Biological, Radiological, Nuclear and Explosives',
    },
    { key: 'Other', label: 'Other' },
  ];

  readonly allowedUrgenciesValues = [
    { key: 'Immediate', label: 'Immediate' },
    { key: 'Expected', label: 'Expected' },
    { key: 'Future', label: 'Future' },
    { key: 'Past', label: 'Past' },
    { key: 'Unknown', label: 'Unknown' },
  ];

  readonly allowedSeveritiesValues = [
    { key: 'Extreme', label: 'Extreme' },
    { key: 'Severe', label: 'Severe' },
    { key: 'Moderate', label: 'Moderate' },
    { key: 'Minor', label: 'Minor' },
    { key: 'Unknown', label: 'Unknown' },
  ];

  readonly allowedCertaintiesValues = [
    { key: 'Observed', label: 'Observed' },
    { key: 'Likely', label: 'Likely' },
    { key: 'Possible', label: 'Possible' },
    { key: 'Unlikely', label: 'Unlikely' },
    { key: 'Unknown', label: 'Unknown' },
  ];

  constructor(
    private apiUtilsService: ApiUtilsService,
    private companyApiService: CompanyApiService,
    private presentationsService: PresentationsService,
    private companyStateService: CompanyStateService,
  ) {}

  isEnabled(): boolean {
    return !!this.alertSettings?.enabled;
  }

  clearMessages() {
    this.errorMessage = '';
    this.apiError = '';
  }

  private _updateSettings(company: Company) {
    this._company = company;
    this.alertKey = company.alertKey;
    this.alertSettings = company.alertSettings;
    this.changeDate = this.toDate(company.changeDate);
    this.changedBy = company.changedBy;

    if (
      this.alertSettings &&
      this.alertSettings.presentationId &&
      !this.alertSettings.templateProductCode &&
      !this.alertSettings.presentationName
    ) {
      void this.loadPresentationName(this.alertSettings.presentationId);
    }
  }

  private toDate(value: Date | string | undefined | null): Date | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    return value instanceof Date ? value : new Date(value);
  }

  private async loadPresentationName(presentationId: string): Promise<void> {
    const presentation =
      await this.presentationsService.getPresentationCached(presentationId);
    this.alertSettings.presentationName = presentation.name;
  }

  loadSettings() {
    this._updateSettings(
      cloneDeep(this.companyStateService.getCopyOfSelectedCompany(true)),
    );
  }

  /** Load alerts state from an already-fetched company object. */
  loadFromCompany(company: unknown) {
    this._updateSettings(cloneDeep(company) as Company);
  }

  async exportAlerts(email: string): Promise<unknown> {
    const companyId = this.companyStateService.getSelectedCompanyId();

    try {
      return await this.companyApiService.exportAlerts(companyId, email);
    } catch (err: any) {
      if (!err || !err.message) {
        throw Error('Could not export alerts');
      }
      throw err;
    }
  }

  async changePresentation() {
    try {
      const presentationDetails =
        await this.presentationsService.openPresentationSelector();

      this.alertSettings.presentationId = presentationDetails[0];
      this.alertSettings.presentationName = presentationDetails[1];

      return true;
    } catch (error) {
      return false;
    }
  }

  async save(): Promise<void> {
    this.clearMessages();
    this.isLoading = true;
    this.errorSaving = false;
    this.missingTemplate = false;

    if (this.alertSettings.templateProductCode) {
      this.alertSettings.presentationId = '';
      this.alertSettings.presentationName = '';
    } else if (!this.alertSettings.presentationId) {
      this.missingTemplate = true;
      this.isLoading = false;
      return;
    }

    const presentationName = this.alertSettings.presentationName || '';
    try {
      const result = await this.companyApiService.updateAlerts(
        this._company.id,
        this._company,
      );
      this.companyStateService.updateCompanySettings(result.item);
      this._updateSettings(result.item);
      this.alertSettings.presentationName = presentationName;
    } catch (error: any) {
      this.errorSaving = true;
      this.errorMessage = 'Failed to update Alerts.';
      this.apiError = this.apiUtilsService.humanReadableError(
        (error && error.result) || error,
      );
      console.error(this.errorMessage, error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Enables CAP Alerts (marks the integration connected) without requiring
   * template selection — configuration continues in the settings dialog.
   *
   * With a default template, a company that has neither a template nor a
   * presentation saved gets that template in the same request, so a connected
   * company always has one. A company that kept an earlier choice keeps it.
   */
  async enable(defaultTemplateProductCode?: string): Promise<void> {
    this.loadSettings();
    if (!this.alertSettings) {
      return;
    }

    const previousEnabled = this.alertSettings.enabled;
    const previousTemplate = this.alertSettings.templateProductCode;
    if (
      defaultTemplateProductCode &&
      !this.alertSettings.templateProductCode &&
      !this.alertSettings.presentationId
    ) {
      this.alertSettings.templateProductCode = defaultTemplateProductCode;
    }
    this.alertSettings.enabled = true;
    try {
      await this.persistAlertSettings();
    } catch (error) {
      this.alertSettings.enabled = previousEnabled;
      this.alertSettings.templateProductCode = previousTemplate;
      throw error;
    }
  }

  /**
   * Disables CAP Alerts while keeping all other alert settings fields.
   */
  async disconnect(): Promise<void> {
    this.loadSettings();
    if (!this.alertSettings) {
      return;
    }

    const previousEnabled = this.alertSettings.enabled;
    this.alertSettings.enabled = false;
    try {
      await this.persistAlertSettings();
    } catch (error) {
      this.alertSettings.enabled = previousEnabled;
      throw error;
    }
  }

  /** Persists current alert settings without the template-selection gate. */
  private async persistAlertSettings(): Promise<void> {
    this.clearMessages();
    this.isLoading = true;
    this.errorSaving = false;

    const presentationName = this.alertSettings.presentationName || '';
    try {
      const result = await this.companyApiService.updateAlerts(
        this._company.id,
        this._company,
      );
      this.companyStateService.updateCompanySettings(result.item);
      this._updateSettings(result.item);
      this.alertSettings.presentationName = presentationName;
    } catch (error: any) {
      this.errorSaving = true;
      this.errorMessage = 'Failed to update Alerts.';
      this.apiError = this.apiUtilsService.humanReadableError(
        (error && error.result) || error,
      );
      console.error(this.errorMessage, error);
      throw error;
    } finally {
      this.isLoading = false;
    }
  }

  async regenerateAlertKey(): Promise<void> {
    this.clearMessages();
    this.isLoading = true;

    try {
      const result = await this.companyApiService.regenerateField(
        this._company.id,
        'alertKey',
      );
      this.alertKey = result.item;
    } catch (error) {
      this.errorMessage = 'Failed to reset Web Service URL.';
      this.apiError = this.apiUtilsService.humanReadableError(error);
      console.error(this.errorMessage, error);
    } finally {
      this.isLoading = false;
    }
  }
}
